import React, { useState, useEffect } from 'react';
import './PrivacyCenter.css';
import serverAPI from '../services/serverAPI';

const PrivacyCenter = () => {
  const [currentView, setCurrentView] = useState('overview');
  const [documents, setDocuments] = useState([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [isLoadingUnsubscribe, setIsLoadingUnsubscribe] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showPhoneForm, setShowPhoneForm] = useState(false);

  // Get URL parameters for email/phone pre-filling
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const email = urlParams.get('email');
    const phone = urlParams.get('phone');
    
    if (email) {
      setUserEmail(email);
      setShowEmailForm(true);
    }
    if (phone) {
      setUserPhone(phone);
      setShowPhoneForm(true);
    }
  }, []);

  // Auto-dismiss success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000); // 5 seconds

      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Fetch legal documents
  useEffect(() => {
    const fetchAllLegalDocuments = async () => {
      try {
        setIsLoadingDocuments(true);
        // Get all legal documents from the database
        const allDocuments = await serverAPI.getAllLegalDocuments();
        setDocuments(allDocuments.legalDocuments);
        setIsLoadingDocuments(false);
      } catch (error) {
        console.error('Error fetching documents:', error);
        setError(error.message || 'Failed to load legal documents. Please try again later.');
        setIsLoadingDocuments(false);
        setDocuments([]);
      }
    };
    fetchAllLegalDocuments();
  }, []);

  const handleEmailUnsubscribe = async (e) => {
    e.preventDefault();
    if (!userEmail.trim()) {
      setError('Please enter a valid email address.');
      return;
    }

    try {
      setIsLoadingUnsubscribe(true);
      setError('');
      
      // Call unsubscribe API
      const response = await serverAPI.unsubscribeEmail(userEmail);
      
      if (response.success) {
        setSuccessMessage('You have been successfully unsubscribed from email communications.');
        setShowEmailForm(false);
      } else {
        setError(response.error || 'Failed to unsubscribe. Please try again.');
      }
    } catch (error) {
      console.log('Email unsubscribe error:', error.message);
      setError(error.message || 'Failed to unsubscribe. Please try again.');
    } finally {
      setIsLoadingUnsubscribe(false);
    }
  };

  const handlePhoneUnsubscribe = async (e) => {
    e.preventDefault();
    if (!userPhone.trim()) {
      setError('Please enter a valid phone number.');
      return;
    }

    try {
      setIsLoadingUnsubscribe(true);
      setError('');
      
      // Call unsubscribe API
      const response = await serverAPI.unsubscribeSMS(userPhone);
      
      if (response.success) {
        setSuccessMessage('You have been successfully unsubscribed from SMS communications.');
        setShowPhoneForm(false);
      } else {
        setError(response.error || 'Failed to unsubscribe. Please try again.');
      }
    } catch (error) {
      console.log('SMS unsubscribe error:', error);
      setError(error.message || 'Failed to unsubscribe. Please try again.');
    } finally {
      setIsLoadingUnsubscribe(false);
    }
  };

  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length === 0) return '';
    if (phoneNumber.length <= 3) {
      return `(${phoneNumber}`;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setUserPhone(formatted);
  };

  const renderOverview = () => (
    <div className="privacy-overview">
      <div className="privacy-header">
        <h1>Privacy Center</h1>
        <p className="privacy-subtitle">
          Manage your privacy preferences and communication settings
        </p>
      </div>

      <div className="privacy-cards">
        <div className="privacy-card" onClick={() => setCurrentView('documents')}>
          <div className="card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="card-content">
            <h3>Terms and Conditions</h3>
            <p>View our privacy policy, terms of service, and other legal documents</p>
          </div>
          <div className="card-arrow">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        <div className="privacy-card" onClick={() => setCurrentView('communications')}>
          <div className="card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="card-content">
            <h3>Communication Preferences</h3>
            <p>Manage your email and SMS communication settings</p>
          </div>
          <div className="card-arrow">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        <div className="privacy-card" onClick={() => setCurrentView('data-rights')}>
          <div className="card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22S2 18 2 12V5L12 2L22 5V12C22 18 12 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="card-content">
            <h3>Your Data Rights</h3>
            <p>Learn about your rights regarding personal data and how to exercise them</p>
          </div>
          <div className="card-arrow">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      </div>

      <div className="privacy-footer">
        <p>
          Questions about your privacy? Contact us at{' '}
          <a href="mailto:privacy@mysimplerx.com">privacy@mysimplerx.com</a>
        </p>
      </div>
    </div>
  );

  const renderDocuments = () => (
    <div className="documents-view">
      <div className="view-header">
        <button 
          className="back-button" 
          onClick={() => {
            setError('');
            setCurrentView('overview');
            setSuccessMessage('');
          }}
          aria-label="Back to overview"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
        <h2>Terms and Conditions</h2>
      </div>
      <div className="view-content">

      {isLoadingDocuments ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading documents...</p>
        </div>
      ) : documents.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h3>No Documents Available</h3>
          <p>There are currently no legal documents available for viewing.</p>
        </div>
      ) : (
        <div className="documents-list">
          {documents.map((document) => (
            <div key={document.id} className="document-item">
                <div className="document-header">
                    <div className="document-title-section">
                        <h3>{document.title} {document.version !== '1.0' && `v${document.version}`}</h3>
                        {document.updated_at && <p>Last Updated: {new Date(document.updated_at).toLocaleDateString()}</p>}
                    </div>
                </div>
              
                <div className="document-text">
                    <div dangerouslySetInnerHTML={{ __html: document.content }} />
                </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );

  const renderCommunications = () => (
    <div className="communications-view">
      <div className="view-header">
        <button 
          className="back-button" 
          onClick={() => {
            setError('');
            setCurrentView('overview');
            setSuccessMessage('');
            setShowEmailForm(false);
            setShowPhoneForm(false);
          }}
          aria-label="Back to overview"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
        <h2>Communication Preferences</h2>
      </div>
      <div className="view-content">
        <div className="communications-content">
        <div className="communication-section">
          <div className="section-header" onClick={() => setShowEmailForm(!showEmailForm)}>
            <div className="section-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="section-content">
              <h3>Email Communications</h3>
              <p>Manage your email subscription preferences</p>
            </div>
            <div className="section-action">
              {successMessage.includes('email') ? (
                <div className="status-badge unsubscribed">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Unsubscribed
                </div>
              ) : (
                <div 
                  className="unsubscribe-toggle"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setShowEmailForm(!showEmailForm);
                    }
                  }}
                >
                  {/* <span className="unsubscribe-text">
                    {showEmailForm ? 'Cancel' : 'Unsubscribe'}
                  </span> */}
                  <div className="unsubscribe-arrow">
                    <span className="material-icons">
                      {showEmailForm ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showEmailForm && (
            <div className="unsubscribe-form">
              <form onSubmit={handleEmailUnsubscribe}>
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    placeholder="Enter your email address"
                    required
                    className="form-control"
                  />
                </div>
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowEmailForm(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={isLoadingUnsubscribe}
                  >
                    {isLoadingUnsubscribe ? 'Processing...' : 'Unsubscribe'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        <div className="communication-section">
          <div className="section-header"  onClick={() => setShowPhoneForm(!showPhoneForm)}>
            <div className="section-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7293C21.7209 20.9845 21.5573 21.2136 21.3521 21.4019C21.1468 21.5901 20.9046 21.7335 20.6407 21.8227C20.3769 21.9119 20.0974 21.9451 19.82 21.92C16.7428 21.5856 13.787 20.5341 11.19 18.85C8.77382 17.3147 6.72533 15.2662 5.18999 12.85C3.49997 10.2412 2.44824 7.27099 2.11999 4.18C2.095 3.90347 2.12787 3.62476 2.21649 3.36162C2.30512 3.09849 2.44756 2.85669 2.63476 2.65162C2.82196 2.44655 3.0498 2.28271 3.30379 2.17052C3.55777 2.05833 3.83233 2.00026 4.10999 2H7.10999C7.59531 1.99522 8.06622 2.16708 8.43376 2.48353C8.8013 2.79999 9.04207 3.23945 9.10999 3.72C9.23662 4.68007 9.47144 5.62273 9.80999 6.53C9.94454 6.88792 9.97366 7.27691 9.8939 7.65088C9.81415 8.02485 9.62886 8.36811 9.35999 8.64L8.08999 9.91C9.51355 12.4135 11.5865 14.4864 14.09 15.91L15.36 14.64C15.6319 14.3711 15.9751 14.1858 16.3491 14.1061C16.7231 14.0263 17.1121 14.0555 17.47 14.19C18.3773 14.5286 19.3199 14.7634 20.28 14.89C20.7658 14.9585 21.2094 15.2032 21.5265 15.5775C21.8437 15.9518 22.0122 16.4296 21.99 16.92H22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="section-content">
              <h3>SMS Communications</h3>
              <p>Manage your text message subscription preferences</p>
            </div>
            <div className="section-action">
              {successMessage.includes('SMS') ? (
                <div className="status-badge unsubscribed">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Unsubscribed
                </div>
              ) : (
                <div 
                  className="unsubscribe-toggle"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setShowPhoneForm(!showPhoneForm);
                    }
                  }}
                >
                  {/* <span className="unsubscribe-text">
                    {showPhoneForm ? 'Cancel' : 'Unsubscribe'}
                  </span> */}
                  <div className="unsubscribe-arrow">
                    <span className="material-icons">
                      {showPhoneForm ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showPhoneForm && (
            <div className="unsubscribe-form">
              <form onSubmit={handlePhoneUnsubscribe}>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    value={userPhone}
                    onChange={handlePhoneChange}
                    placeholder="(555) 123-4567"
                    required
                    className="form-control"
                    maxLength={14}
                  />
                </div>
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowPhoneForm(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={isLoadingUnsubscribe}
                  >
                    {isLoadingUnsubscribe ? 'Processing...' : 'Unsubscribe'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        <div className="communication-info">
          <h4>About Unsubscribing</h4>
          <ul>
            <li>You can unsubscribe from marketing communications at any time</li>
            <li>Important account-related communications will still be sent</li>
            <li>Changes may take up to 24 hours to take effect</li>
            <li>You can resubscribe at any time by contacting support</li>
          </ul>
        </div>
        </div>
      </div>
    </div>
  );

  const renderDataRights = () => (
    <div className="data-rights-view">
      <div className="view-header">
        <button 
          className="back-button" 
          onClick={() => {
            setError('');
            setCurrentView('overview');
            setSuccessMessage('');
          }}
          aria-label="Back to overview"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
        <h2>Your Data Rights</h2>
      </div>
      <div className="view-content">
        <div className="data-rights-content">
        <div className="rights-section">
          <h3>Right to Access</h3>
          <p>You have the right to request access to the personal data we hold about you.</p>
        </div>

        <div className="rights-section">
          <h3>Right to Rectification</h3>
          <p>You have the right to request correction of inaccurate or incomplete personal data.</p>
        </div>

        <div className="rights-section">
          <h3>Right to Erasure</h3>
          <p>You have the right to request deletion of your personal data in certain circumstances.</p>
        </div>

        <div className="rights-section">
          <h3>Right to Restrict Processing</h3>
          <p>You have the right to request restriction of processing of your personal data.</p>
        </div>

        <div className="rights-section">
          <h3>Right to Data Portability</h3>
          <p>You have the right to receive your personal data in a structured, commonly used format.</p>
        </div>

        <div className="rights-section">
          <h3>Right to Object</h3>
          <p>You have the right to object to processing of your personal data for certain purposes.</p>
        </div>

        <div className="contact-section">
          <h3>Exercise Your Rights</h3>
          <p>To exercise any of these rights, please contact us:</p>
          <div className="contact-info">
            <p><strong>Email:</strong> privacy@mysimplerx.com</p>
            <p><strong>Phone:</strong> 1-800-746-7537</p>
            <p><strong>Response Time:</strong> We will respond to your request within 30 days</p>
          </div>
        </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="privacy-center">
      <div className="privacy-container">
        {error && (
          <div className="alert alert-error">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {error}
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {successMessage}
          </div>
        )}

        {currentView === 'overview' && renderOverview()}
        {currentView === 'documents' && renderDocuments()}
        {currentView === 'communications' && renderCommunications()}
        {currentView === 'data-rights' && renderDataRights()}
      </div>
    </div>
  );
};

export default PrivacyCenter;
